import React, { useEffect, useState } from "react";
import Pagination from "../../../../../components/Pagination";
import { useAppContext } from "../../../../../context/AppContext";
import CardLoader from "../../../../../components/CardLoader";
import Toast from "../../../../../components/Toast";
import ButtonLoader from "../../../../../components/ButtonLoader";
import { highlightText } from "../../../../../utils/highlight";
import Transition from "../../../../../components/Transition";
import { AnimatePresence } from "framer-motion";

import { ProductsData } from "../../../network/api/ProductsData";
import { ProductData } from "../../../network/api/ProductData";
import { Link } from "react-router-dom";

export default function ProductList({ setEditMode, selectedProduct, setSelectedProduct }) {
    const { appData } = useAppContext();
    const [productList, setProductList] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [productReload, setProductReload] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [toastConfig, setToastConfig] = useState({
        message: "",
        type: "primary",
    });

    const [pageProps, setPageProps] = useState({
        page: 1,
        totalPages: 1,
        totalEntries: 0,
        entryLimit: 10,
        visiblePages: []
    });

    const [pageFilters, setPageFilters] = useState({
        app_id: appData.app_id || null,
        page: null,
        name: null,
        slug: null,
        limit: 10
    });

    const handleEditSwitch = (e, product = null) => {
        e.preventDefault();
        setEditMode(true);
        setSelectedProduct(product);
    };

    const handlePageChange = (page) => {
        if (page >= 1 && page <= pageProps.totalPages) {
            setPageProps(prev => ({ ...prev, page }));
            setPageFilters(prev => ({ ...prev, page }));
        }
    };

    const handleLimitChange = (value) => {
        setPageFilters(prev => ({ ...prev, limit: value }));
    };

    const [searchValue, setSearchValue] = useState("");
    const handleSearchChange = (e) => {
        const { value } = e.target;
        setSearchValue(value);
        if (value.length >= 3) {
            setPageFilters(prev => ({ ...prev, name: value, slug: value, page: 1 }));
        } else if (value.length === 0) {
            setPageFilters(prev => ({ ...prev, name: null, slug: null, page: 1 }));
        }
    };

    const handleDeleteProduct = () => {
        setIsLoading(true);
        setTimeout(async () => {
            try {
                const response = await ProductData({ method: "DELETE", id: selectedProduct?.id });
                if (response.status === "success") {
                    setProductList((prevs) => prevs.filter((item) => item.id !== selectedProduct?.id));
                    document.querySelector('#deleteModalCenter .btn-close')?.click();
                    setToastConfig({ visible: true, message: `${response.message} Deleting Product ${selectedProduct?.name}`, type: response.status === "error" ? "danger" : response.status });
                } else {
                    setToastConfig({ visible: true, message: `${response.message}  Deleting Product ${selectedProduct?.name}`, type: (response.status) === "error" ? "danger" : response.status });
                }
            } catch (err) {
                setToastConfig({ visible: true, message: "Delete failed", type: "danger" });
            } finally {
                setIsLoading(false);
                setConfirmDelete(false);
            }
        }, 500);
    };

    useEffect(() => {
        const fetchLogs = async () => {
            setIsLoading(true);
            setErrorMessage(null);

            try {
                const response = await ProductsData({ app_id: appData.id, pageFilters: pageFilters });
                
                if (response.status === "success") {

                    if (JSON.stringify(response.data.data) !== JSON.stringify(productList)) {
                        setProductList(response.data.data);
                        setPageProps({
                            page: response.data.page,
                            totalPages: response.data.total_pages,
                            totalEntries: response.data.total_records,
                            entryLimit: response.data.limit,
                            visiblePages: response.data.visible_pages,
                        })
                    }

                } else {
                    setErrorMessage({ code: response.code, message: response.message });
                }
            } catch (err) {
                setErrorMessage({ code: "500", message: "Failed to load users." });
            } finally {
                setIsLoading(false);
            }
        };

        fetchLogs();
    }, [pageFilters, productList, productReload, appData]);

    return (
        <>
            <AnimatePresence mode="wait">
                <Transition key={"product-list-page"}>
                    <Toast
                        message={toastConfig.message}
                        type={toastConfig.type}
                        visible={toastConfig.visible}
                        setVisible={(visible) => setToastConfig({ ...toastConfig, visible })}
                    />
                    <div className="card custom-card">
                        {errorMessage && <CardLoader errorMessage={errorMessage} onRetry={() => setProductReload(!productReload)} />}

                        <div className="card-header d-flex justify-content-between align-items-center">
                            <h4 className="card-title">Products</h4>
                            <button className="btn btn-primary btn-sm" onClick={(e) => handleEditSwitch(e)}>
                                <i className="fa fa-plus"></i> Add
                            </button>
                        </div>
                        <div className="card-body">
                            <div className="table-responsive dataTables_wrapper">
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <label>
                                        Show{" "}
                                        <select value={pageProps.entryLimit} onChange={(e) => handleLimitChange(Number(e.target.value))}>
                                            <option value="10">10</option>
                                            <option value="25">25</option>
                                            <option value="50">50</option>
                                            <option value="100">100</option>
                                        </select>{" "}
                                        entries
                                    </label>
                                    <label>
                                        Search:
                                        <input type="search" value={searchValue} onChange={handleSearchChange} />
                                    </label>
                                </div>
                                <table className="table table-striped">
                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>Name</th>
                                            <th>Slug</th>
                                            <th>Type</th>
                                            <th>Payment</th>
                                            <th>Duration</th>
                                            <th>Price</th>
                                            <th>Status</th>
                                            <th>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {productList.map((item, index) => (
                                            <tr key={item.id}>
                                                <td>{(pageProps.entryLimit * (pageProps.page - 1)) + (index + 1)}</td>
                                                <td><Link to={`/shop/${item.slug}`}><strong>{highlightText(item.name, searchValue)}</strong></Link></td>
                                                <td><Link to={`/shop/${item.slug}`}><strong>{highlightText(item.slug, searchValue)}</strong></Link></td>
                                                <td>{item.type}</td>
                                                <td>{item.payment_type}</td>
                                                <td>{item.duration} Days</td>
                                                <td>{item.price}</td>
                                                <td>{item.status}</td>
                                                <td>
                                                    <div className="d-flex">
                                                        <a href="/" onClick={(e) => handleEditSwitch(e, item)} className="btn btn-primary shadow btn-xs sharp me-1"><i className="fa fa-pencil-alt"></i></a>
                                                        <button
                                                            onClick={() => { setSelectedProduct(item) }}
                                                            data-bs-target="#deleteModalCenter"
                                                            data-bs-toggle="modal"
                                                            className="btn btn-danger shadow btn-xs sharp"
                                                        >
                                                            <i className="fa fa-trash"></i>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                <Pagination pageProps={pageProps} onPageChange={handlePageChange} />
                            </div>
                        </div>
                    </div>
                </Transition>

            </AnimatePresence>

            <div className="modal fade" id="deleteModalCenter">
                <div className="modal-dialog modal-dialog-centered" role="document">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">Delete Product</h5>
                            <button type="button" className="btn-close" data-bs-dismiss="modal">
                            </button>
                        </div>
                        <div className="modal-body">
                            <h6>Are you sure you want to delete this <strong>{selectedProduct?.title}</strong>?</h6>
                            <p>This action will also delete all associated orders, pictures and orders items.</p>
                            <div className="form-check mt-3">
                                <input
                                    type="checkbox"
                                    className="form-check-input"
                                    id="confirmDeleteCheck"
                                    checked={confirmDelete}
                                    onChange={(e) => setConfirmDelete(e.target.checked)}
                                />
                                <label className="form-check-label" htmlFor="confirmDeleteCheck">
                                    I understand the consequences of this action.
                                </label>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-dark light" data-bs-dismiss="modal">Cancel</button>
                            <button
                                type="button"
                                className="btn btn-danger"
                                disabled={!confirmDelete || isLoading}
                                onClick={handleDeleteProduct}
                            >
                                {isLoading ? <ButtonLoader /> : "Delete"}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}
